/* eslint-disable */
import type { OTPInfo, PasswordInfo } from "../../../access/sync_values.pb";
import type { Headers } from "../../../common/protocols.pb";
import type {
  GetLinkedAccountsRequest,
  GetLinkedAccountsResponse,
  GetWebLoginConfigRequest,
  GetWebLoginConfigResponse,
  InitiateSessionRequest,
  InitiateSessionResponse,
  RegisterEmailRequest,
  RegisterEmailResponse,
  RegisterSmsRequest,
  RegisterSmsResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  VerifySmsRequest,
  VerifySmsResponse,
} from "../../../franklin/app.pb";
import type { WebLoginConfig } from "../../../franklin/common.pb";
import type { SetPasswordRequest, SetPasswordResponse } from "../../security/api.pb";
import type {
  CreateProfileRequest,
  CreateProfileResponse,
  GetProfilesByAccountholderRequest,
  GetProfilesByAccountholderResponse,
  GetProfilesRequest,
  GetProfilesResponse,
  SetAccountholderProfileRequest,
  SetAccountholderProfileResponse,
  SwitchProfileRequest,
  SwitchProfileResponse,
} from "./AccountholderProfile.pb";
import type { GetIrsInfoRequest, GetIrsInfoResponse } from "./GetIrsInfoProto.pb";
import type { GetLoginAttemptsRequest, GetLoginAttemptsResponse } from "./GetLoginAttempts.pb";

export interface TrustDeviceRequest {
  customer_token?: string;
  device_id?: string;
}

export interface TrustDeviceResponse {
  status?: TrustDeviceResponseStatus;
}

export enum TrustDeviceResponseStatus {
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

export interface ResetPasswordRequest {
  customer_token?: string;
}

export interface ResetPasswordResponse {
  status?: ResetPasswordResponseStatus;
}

export enum ResetPasswordResponseStatus {
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
}

export interface GetSettingsPageDetailsRequest {
  customer_token?: string;
}

export interface GetSettingsPageDetailsResponse {
  password_info?: PasswordInfo;
  otp_info?: OTPInfo;
}

export interface GetLoginDetailsRequest {
  customer_token?: string;
  app_token?: string;
  /**
   * If a login cannot be found using customer token and app token, janus can return login location
   * information translated via GeoIP with a provided IP Address
   */
  ip_address?: string;
}

export interface GetLoginDetailsResponse {
  device_description?: string;
  last_login_time?: string;
  last_login_location?: string;
  login_location_url?: string;
  last_login_time_instant?: number;
}

export interface GetLoginDetailsResponseWrapper {
  get_login_details_response?: GetLoginDetailsResponse;
  customer_token?: string;
  app_token?: string;
}

export interface GetWebLoginConfigInternalRequest {
  customer_token?: string;
  app_token?: string;
}

export interface GetWebLoginConfigInternalResponse {
  web_login_config?: WebLoginConfig;
}

export interface AuthenticateWebLoginTokenRequest {
  headers?: Headers;
  web_login_token?: string;
}

export interface AuthenticateWebLoginTokenResponse {
  result?: AuthenticateWebLoginTokenResponseResult;
  /**
   * These fields are only returned if the result is GRANTED.
   * This is a newly granted session that was given based on the provided web_login_token.
   */
  session_cookie?: string;
  app_token_cookie?: string;
}

export enum AuthenticateWebLoginTokenResponseResult {
  /** UNAUTHORIZED - The client should receive an HTTP 401 Unauthorized response. */
  UNAUTHORIZED = "UNAUTHORIZED",
  /** INVALID - The client should receive an HTTP 400 Bad Request response or other failure. */
  INVALID = "INVALID",
  /** GRANTED - The web_login_token that was provided was valid, and a session has been granted and returned. */
  GRANTED = "GRANTED",
}

export interface RevokeWebLoginTokensRequest {
  customer_token?: string;
  app_token?: string;
}

export interface RevokeWebLoginTokensResponse {
  num_tokens_revoked?: number;
}

export interface JanusService {
  InitiateSession(request: InitiateSessionRequest): Promise<InitiateSessionResponse>;
  GetLinkedAccounts(request: GetLinkedAccountsRequest): Promise<GetLinkedAccountsResponse>;
  RegisterEmail(request: RegisterEmailRequest): Promise<RegisterEmailResponse>;
  RegisterSms(request: RegisterSmsRequest): Promise<RegisterSmsResponse>;
  VerifyEmail(request: VerifyEmailRequest): Promise<VerifyEmailResponse>;
  VerifySms(request: VerifySmsRequest): Promise<VerifySmsResponse>;
  ResetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse>;
  SetPassword(request: SetPasswordRequest): Promise<SetPasswordResponse>;
  GetSettingsPageDetails(request: GetSettingsPageDetailsRequest): Promise<GetSettingsPageDetailsResponse>;
  GetLoginDetails(request: GetLoginDetailsRequest): Promise<GetLoginDetailsResponse>;
  GetWebLoginConfig(request: GetWebLoginConfigRequest): Promise<GetWebLoginConfigResponse>;
  TrustDevice(request: TrustDeviceRequest): Promise<TrustDeviceResponse>;
  GetIrsInfo(request: GetIrsInfoRequest): Promise<GetIrsInfoResponse>;
  GetWebLoginConfigInternal(request: GetWebLoginConfigInternalRequest): Promise<GetWebLoginConfigInternalResponse>;
  AuthenticateWebLoginToken(request: AuthenticateWebLoginTokenRequest): Promise<AuthenticateWebLoginTokenResponse>;
  RevokeWebLoginTokens(request: RevokeWebLoginTokensRequest): Promise<RevokeWebLoginTokensResponse>;
  GetProfiles(request: GetProfilesRequest): Promise<GetProfilesResponse>;
  SwitchProfile(request: SwitchProfileRequest): Promise<SwitchProfileResponse>;
  CreateProfile(request: CreateProfileRequest): Promise<CreateProfileResponse>;
  GetLoginAttempts(request: GetLoginAttemptsRequest): Promise<GetLoginAttemptsResponse>;
  SetAccountholderProfileInternal(request: SetAccountholderProfileRequest): Promise<SetAccountholderProfileResponse>;
  GetProfilesByAccountholderInternal(
    request: GetProfilesByAccountholderRequest,
  ): Promise<GetProfilesByAccountholderResponse>;
}
